import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Login from './Login';
import JoinCommunity from './JoinCommunity';
import AuthIllustration from '../../assets/auth.png';
import Logo from '../../assets/appLogos/UTrust.png';
import { fetchGroupsList } from 'store/slices/loginSignupSlice';

const ChoiceScreen: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState<'signin' | 'join-community'>('signin');
  const [flow, setFlow] = useState<'signin' | 'join-community'>('signin');
  const [initialGroupId, setInitialGroupId] = useState<string | undefined>(undefined);

  // Fetch groups once when component mounts
  useEffect(() => {
    dispatch(fetchGroupsList() as any);
  }, [dispatch]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');

    if (tab === 'join-community') {
      setFlow('join-community');
      setActiveTab('join-community');
      const gid = queryParams.get('groupId');
      if (gid) setInitialGroupId(gid);
      if (!gid) setInitialGroupId(undefined);
      return;
    }

    if (tab === 'signin') {
      setFlow('signin');
      setActiveTab('signin');
    }
  }, [location.search]);

  const colors = {
    primary: '#2BA699',
    secondary: '#009688',   
    accent: '#FFBEA3',
    background: '#FFFFFF',
    cardBg: 'rgba(255, 255, 255, 0.97)',
    textPrimary: '#2BA699', 
    textSecondary: '#7F7F7F',
    success: '#7DBE8E',
    lightGray: '#E4E4E7',
    darkGray: '#000000',
    border: '#E4E4E7'      
  };

  return (
    <Box sx={{
      minHeight: '100dvh',
      width: '100dvw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#F5F5F5',
      overflow: 'hidden'
    }}>
      {/* Main Container: Compact size */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          width: isSmallScreen ? '100%' : '980px',
          height: isSmallScreen ? 'auto' : '580px',
          background: colors.background,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          position: 'relative',
        }}
      >
          {/* LEFT PANEL - Illustration Background */}
          <Box sx={{
             width: isSmallScreen ? '100%' : '490px',
        height: isSmallScreen ? 'auto' : '100%',
  background: colors.primary,
  position: 'relative',
  overflow: 'hidden',
  p: isSmallScreen ? 3 : '40px 30px 0px 30px',
        }}>
          {/* Content Layer */}
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography sx={{
              fontFamily: 'Roboto',
              fontWeight: 600,
              fontSize: '26px',
              lineHeight: '110%',
              color: '#FFFFFF',
              letterSpacing: '-0.02em',
              mb: '12px'
            }}>
              UTrust Platforms
            </Typography>
            <Typography sx={{
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '140%',
              color: '#C4C4C4',
              letterSpacing: '-0.02em',
              maxWidth: '350px'
            }}>
              Access your account securely to manage information, collaborate with your community, and stay updated with important activity.
            </Typography>
          </Box>

          {/* Illustration - More prominent positioning */}
          <Box
            component="img"
            src={AuthIllustration}
            sx={{
              position: isSmallScreen ? 'relative' : 'absolute',
              width: isSmallScreen ? '100%' : '450px',
              height: isSmallScreen ? 'auto' : '450px',
              maxWidth: '100%',
              maxHeight: isSmallScreen ? '250px' : 'auto',
              top: isSmallScreen ? 'auto' : '180px',
              objectFit: 'contain',
              zIndex: 1,
              mt: isSmallScreen ? 2 : 0,
            }}
          />
        </Box>

        {/* RIGHT PANEL - Form Logic */}
        <Box sx={{
          width: isSmallScreen ? '100%' : '490px',
          p: isSmallScreen ? 3 : '16px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        
         <Box
  sx={{
    width: { xs: '220px', md: '260px' },
    height: { xs: '90px', md: '110px' },
    mb: '2px',
    alignSelf: 'flex-start',
    mt: { xs: '-8px', md: '-50px' },
    ml: { xs: '-8px', md: '-100px' },
  }}
>
  <img
    src={Logo}
    alt="UTrust"
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    }}
  />
</Box>


          {/* Toggle Buttons - Sign In | Join Community */}
          <Box sx={{ display: 'flex', width: '100%', height: '42px', mt: '2px', mb: '2px', gap: 0 }}>
            <Button
              onClick={() => {
                setFlow('signin');
                setActiveTab('signin');
              }}
              sx={{
                flex: 1,
                background: flow === 'signin' ? colors.primary : 'transparent',
                color: flow === 'signin' ? '#FFFFFF' : colors.primary,
                border: `1px solid ${colors.primary}`,
                borderRadius: '8px 0px 0px 8px',
                textTransform: 'none',
                fontFamily: 'Roboto',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': { background: flow === 'signin' ? colors.primary : alpha(colors.primary, 0.05) }
              }}
            >
              Sign In
            </Button>
            <Button
              onClick={() => {
                setFlow('join-community');
                setActiveTab('join-community');
              }}
              sx={{
                flex: 1,
                background: flow === 'join-community' ? colors.primary : 'transparent',
                color: flow === 'join-community' ? '#FFFFFF' : colors.primary,
                border: `1px solid ${colors.primary}`,
                borderRadius: '0px 8px 8px 0px',
                textTransform: 'none',
                fontFamily: 'Roboto',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': { background: flow === 'join-community' ? colors.primary : alpha(colors.primary, 0.05) }
              }}
            >
              Join Community
            </Button>
          </Box>

          {flow === 'signin' && (
            <Typography sx={{
              width: '100%',
              textAlign: 'center',
              color: colors.textSecondary,
              fontSize: '14px',
              mb: '8px',
              fontFamily: 'Roboto'
            }}>
              Welcome back! Sign in to your account.
            </Typography>
          )}
          {flow === 'join-community' && (
            <Typography sx={{
              width: '100%',
              textAlign: 'center',
              color: colors.textSecondary,
              fontSize: '14px',
              mb: '8px',
              fontFamily: 'Roboto'
            }}>
              Create your account
            </Typography>
          )}

          <AnimatePresence mode="wait">
            {flow === 'signin' ? (
              <motion.div key="signin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Login onBack={() => setFlow('signin')} colors={colors} />
              </motion.div>
            ) : flow === 'join-community' ? (
              <motion.div key="join-community" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <JoinCommunity
                  onBack={() => setFlow('signin')}
                  colors={colors}
                  initialGroupId={initialGroupId}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </Box>
      </motion.div>
    </Box>
  );
};

export default ChoiceScreen;